import type { ReactNode } from "react";

import { ConmutadorDeVista, type VistaDelMantenedor } from "@/components/plantillas/ConmutadorDeVista";
import { PuntoDeObligatoriedad } from "@/components/plantillas/etiquetas";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

/**
 * La franja de arriba del mantenedor: el interruptor "Plantillas | Maestro de
 * ítems | Formatos de salida" a la izquierda y, a la derecha, lo que cada pantalla tenga que decir
 * sobre el guardado. Una línea clara la separa del contenido.
 *
 * Es la misma en las tres pantallas para que el interruptor no se mueva al
 * pasar de una a otra.
 */
export function CabeceraDelMantenedor({
  vista,
  children,
}: {
  vista: VistaDelMantenedor;
  children?: ReactNode;
}) {
  return (
    <header className="border-divisor mb-6 flex flex-wrap items-center gap-x-4 gap-y-3 border-b pb-5">
      <ConmutadorDeVista vista={vista} />
      {children ? <div className="ml-auto flex items-center gap-3">{children}</div> : null}
    </header>
  );
}

/**
 * Estado del borrador + "Descartar" + "Guardar cambios". Limpio, el botón es
 * un gris neutro: el verde se reserva para cuando hay algo que guardar.
 */
export function BarraDeGuardado({
  hayCambios,
  guardando,
  onDescartar,
  onGuardar,
}: {
  hayCambios: boolean;
  guardando: boolean;
  onDescartar: () => void;
  onGuardar: () => void;
}) {
  return (
    <>
      <span className="text-ink-hint flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap">
        <PuntoDeObligatoriedad
          obligatoriedad={hayCambios ? "condicional" : "obligatorio"}
          className="size-[0.4375rem]"
        />
        {hayCambios ? "Cambios sin guardar" : "Todo guardado"}
      </span>
      {hayCambios ? (
        <Button variant="outline" onClick={onDescartar} disabled={guardando}>
          Descartar
        </Button>
      ) : null}
      <Button
        disabled={!hayCambios || guardando}
        onClick={onGuardar}
        className="disabled:bg-ink-ghost disabled:text-white disabled:opacity-100 disabled:shadow-none"
      >
        {guardando ? <Spinner /> : null}
        Guardar cambios
      </Button>
    </>
  );
}
